/**
 * ByteGurukul design tokens.
 *
 * The codebase styles components with inline style objects, so the design
 * system ships as plain JS rather than a CSS framework. Import these instead
 * of hard-coding hex values or pixel sizes in new work.
 */

export const color = {
  // Brand — indigo through violet, matching the existing hero gradient.
  brand: "#4F46E5",
  brandDark: "#4338CA",
  brandSoft: "#EEF2FF",
  accent: "#7C3AED",

  // Ink and neutrals, biased slightly cool so they sit under the brand hue.
  ink: "#0F172A",
  body: "#334155",
  muted: "#64748B",
  faint: "#94A3B8",
  line: "#E2E8F0",
  lineSoft: "#F1F5F9",
  surface: "#FFFFFF",
  canvas: "#F8FAFC",

  // Semantic — kept separate from the brand hue.
  success: "#0F766E",
  successSoft: "#ECFDF5",
  warning: "#B45309",
  warningSoft: "#FFFBEB",
  danger: "#B91C1C",
  dangerSoft: "#FEF2F2",
};

/** Fluid type: scales between mobile and desktop without a media query. */
export const type = {
  display: "clamp(32px, 5vw, 52px)",
  h1: "clamp(28px, 4vw, 42px)",
  h2: "clamp(23px, 3vw, 32px)",
  h3: "clamp(18px, 2vw, 22px)",
  lead: "clamp(16px, 1.5vw, 18px)",
  body: "15.5px",
  small: "13.5px",
  micro: "11.5px",
  sans: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  serif: "'Iowan Old Style', 'Palatino Linotype', Georgia, serif",
  mono: "ui-monospace, 'Cascadia Mono', Consolas, monospace",
};

/**
 * Type scale for content-dense pages — catalogues, dashboards, course
 * players.
 *
 * The `type` scale above is tuned for marketing heroes, where a 42px heading
 * is doing persuasive work. Reusing it on a listing page makes the UI feel
 * zoomed in: a course catalogue is scanned, not read, so headings sit closer
 * to body size the way Coursera, Udemy and Great Learning set theirs.
 */
export const appType = {
  pageTitle: "clamp(23px, 2.4vw, 29px)",
  sectionTitle: "clamp(17px, 1.7vw, 20px)",
  cardTitle: "16px",
  lead: "15.5px",
  body: "14.5px",
  small: "13px",
  micro: "11.5px",
};

export const space = {
  xs: "6px",
  sm: "12px",
  md: "20px",
  lg: "32px",
  xl: "48px",
  xxl: "72px",
  section: "clamp(48px, 7vw, 88px)",
};

export const radius = {
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  pill: "999px",
};

export const shadow = {
  sm: "0 1px 2px rgba(15, 23, 42, 0.05)",
  md: "0 4px 12px rgba(15, 23, 42, 0.06)",
  lg: "0 12px 32px rgba(15, 23, 42, 0.09)",
  brand: "0 10px 24px rgba(79, 70, 229, 0.22)",
};

/** Page shell: one max-width and one gutter, used by every new page. */
export const container = {
  width: "100%",
  maxWidth: "1180px",
  margin: "0 auto",
  padding: "0 clamp(16px, 4vw, 32px)",
  boxSizing: "border-box",
};

export const section = {
  ...container,
  paddingTop: space.section,
  paddingBottom: space.section,
};

/** Auto-fit grid that can never overflow its container. */
export const autoGrid = (min = 280, gap = space.md) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
  gap,
});

export const card = {
  background: color.surface,
  border: `1px solid ${color.line}`,
  borderRadius: radius.lg,
  boxShadow: shadow.sm,
  padding: space.md,
  minWidth: 0,
};

export const button = {
  primary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "13px 26px",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "inherit",
    color: "#fff",
    background: `linear-gradient(135deg, ${color.brand} 0%, ${color.accent} 100%)`,
    border: "none",
    borderRadius: radius.md,
    boxShadow: shadow.brand,
    textDecoration: "none",
    cursor: "pointer",
  },
  secondary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 25px",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "inherit",
    color: color.brand,
    background: color.surface,
    border: `1.5px solid ${color.line}`,
    borderRadius: radius.md,
    textDecoration: "none",
    cursor: "pointer",
  },
};

export const eyebrow = {
  display: "inline-block",
  fontSize: type.micro,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color.brand,
  marginBottom: space.sm,
};
